import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UserX, Calendar, AlertTriangle, Trash2, CheckSquare, Square, Loader2 } from 'lucide-react';
import deleteUser from '../api/DeleteUser';

const CleanupMembersModal = ({ isOpen, onClose, members = [], onRefresh }) => {
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [isDeleting, setIsDeleting] = useState(false);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (isOpen) {
            setSelectedIds(new Set(members.map(m => m.ID || m.id)));
        }
    }, [isOpen, members]);

    if (!isOpen) return null;

    const toggleSelectAll = () => {
        if (selectedIds.size === members.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(members.map(m => m.ID || m.id)));
        }
    };

    const toggleSelect = (id) => {
        const newSelected = new Set(selectedIds);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedIds(newSelected);
    };

    const handleDeleteSelected = async () => {
        if (selectedIds.size === 0) return;

        const confirmMsg = `Êtes-vous sûr de vouloir supprimer ${selectedIds.size} membre(s) ? Cette action est irréversible.`;
        if (!window.confirm(confirmMsg)) return;

        setIsDeleting(true);
        const idsArray = Array.from(selectedIds);
        let completed = 0;

        try {
            for (const id of idsArray) {
                await deleteUser(id);
                completed++;
                setProgress(Math.round((completed / idsArray.length) * 100));
            }
            alert(`${completed} membre(s) supprimé(s) avec succès.`);
            if (onRefresh) await onRefresh();
            onClose();
        } catch (error) {
            console.error("Cleanup failed:", error);
            alert("Une erreur est survenue lors de la suppression. Certains membres n'ont peut-être pas été supprimés.");
            if (onRefresh) await onRefresh();
        } finally {
            setIsDeleting(false);
            setProgress(0);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Inconnue";
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getDaysSinceUpdate = (dateString) => {
        if (!dateString) return 0;
        const diffTime = Math.abs(new Date() - new Date(dateString));
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
                >
                    {/* Header */}
                    <div className="p-6 border-b border-white/10 flex items-center justify-between bg-red-500/10">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-red-500/20 text-red-400">
                                <UserX size={24} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white">Nettoyage des membres</h3>
                                <p className="text-sm text-slate-400">{members.length} membres inactifs depuis {'>'} 300 jours</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            {members.length > 0 && (
                                <button
                                    onClick={toggleSelectAll}
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-sm font-medium text-slate-300 hover:bg-white/10 transition-colors"
                                    disabled={isDeleting}
                                >
                                    {selectedIds.size === members.length ? <CheckSquare size={16} className="text-primary" /> : <Square size={16} />}
                                    Tout sélectionner
                                </button>
                            )}
                            <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-full" disabled={isDeleting}>
                                <X size={24} />
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 overflow-y-auto custom-scrollbar relative">
                        {isDeleting && (
                            <div className="absolute inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex flex-col items-center justify-center space-y-4">
                                <div className="relative w-20 h-20">
                                    <div className="absolute inset-0 border-4 border-primary/20 rounded-full"></div>
                                    <div
                                        className="absolute inset-0 border-4 border-primary rounded-full border-t-transparent animate-spin"
                                    ></div>
                                    <div className="absolute inset-0 flex items-center justify-center font-bold text-white">
                                        {progress}%
                                    </div>
                                </div>
                                <p className="text-lg font-medium text-white">Suppression en cours...</p>
                                <p className="text-sm text-slate-400 px-10 text-center">Veuillez ne pas fermer cette fenêtre pendant le processus.</p>
                            </div>
                        )}

                        {members.length > 0 ? (
                            <div className="space-y-4">
                                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-start gap-3">
                                    <AlertTriangle className="text-amber-500 shrink-0" size={20} />
                                    <p className="text-sm text-amber-200/80">
                                        Ces membres n'ont pas été mis à jour depuis plus de 10 mois.
                                        Cochez ceux que vous souhaitez supprimer définitivement de la base de données.
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {members.map((member) => (
                                        <div
                                            key={member.ID || member.id}
                                            onClick={() => !isDeleting && toggleSelect(member.ID || member.id)}
                                            className={`flex items-center gap-4 p-4 rounded-xl border transition-all cursor-pointer ${selectedIds.has(member.ID || member.id)
                                                    ? 'bg-primary/10 border-primary/30 ring-1 ring-primary/20'
                                                    : 'bg-white/5 border-white/5 hover:bg-white/10'
                                                } ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            <div className={`shrink-0 ${selectedIds.has(member.ID || member.id) ? 'text-primary' : 'text-slate-600'}`}>
                                                {selectedIds.has(member.ID || member.id) ? <CheckSquare size={20} /> : <Square size={20} />}
                                            </div>
                                            <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 border border-white/10">
                                                <img src={member.avatar} alt="" className="w-full h-full object-cover" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="text-white font-medium truncate">{member.name}</h4>
                                                <p className="text-xs text-slate-400">{member.role} • {member.classe || "Sans classe"}</p>
                                                <div className="flex items-center gap-1.5 mt-1 text-[10px] text-slate-500 font-medium uppercase">
                                                    <Calendar size={10} />
                                                    MAJ : {formatDate(member.UpdatedAt)} ({getDaysSinceUpdate(member.UpdatedAt)} j)
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-20 bg-slate-800/10 rounded-2xl border border-dashed border-white/5">
                                <UserX className="mx-auto text-slate-700 mb-4" size={48} />
                                <p className="text-slate-400 text-lg">Aucun ancien membre à effacer</p>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-white/10 bg-black/20 flex items-center justify-between">
                        <p className="text-sm text-slate-500">
                            {selectedIds.size} membre(s) sélectionné(s)
                        </p>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={onClose}
                                disabled={isDeleting}
                                className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                            >
                                Annuler
                            </button>
                            {members.length > 0 && (
                                <button
                                    onClick={handleDeleteSelected}
                                    disabled={selectedIds.size === 0 || isDeleting}
                                    className="px-6 py-2 bg-red-600 hover:bg-red-500 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                                >
                                    {isDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                                    Supprimer la sélection
                                </button>
                            )}
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default CleanupMembersModal;
